/**
 * Formatação legível de regras + utilidades de MODELO DE REGRA (DEV-7414).
 * Fonte única dos rótulos de efeito/operador (reusada pelo RegraCondicionalBuilder,
 * tooltip do campo, preview e aba de modelos).
 */

export const EFEITOS = [
  { value: 'obrigatorio', label: 'obrigatório' },
  { value: 'opcional', label: 'opcional' },
  { value: 'visivel', label: 'visível' },
  { value: 'oculto', label: 'oculto' },
  { value: 'desabilitar', label: 'desabilitado' },
  { value: 'rotular', label: 'renomeia para' },
  { value: 'bloqueia_cadastro', label: 'cadastro bloqueado' },
  { value: 'quantidade', label: 'quantidade' },
];

export const OPERADORES = [
  { value: 'igual', label: 'é igual a' },
  { value: 'em', label: 'está na lista' },
  { value: 'comeca_com', label: 'começa com' },
  { value: 'preenchido', label: 'está preenchido' },
];

export const labelEfeito = (v) => (EFEITOS.find((e) => e.value === v) || {}).label || v;
export const labelOperador = (v) => (OPERADORES.find((o) => o.value === v) || {}).label || v;
export const labelGatilho = (chave, gatilhos = []) => {
  const g = (gatilhos || []).find((x) => x.campo === chave);
  return g ? g.label : chave;
};

const formatarValor = (valor) =>
  Array.isArray(valor) ? valor.join(', ') : valor == null ? '' : String(valor);

function textoCondicao(cond, gatilhos) {
  // Gatilho booleano (tipo checkbox) → mostra Sim/Não em vez do literal 'true'/'false'.
  const g = (gatilhos || []).find((x) => x.campo === cond.chave);
  const ehBooleano = g && g.tipo === 'checkbox';

  let valor;
  if (cond.operador === 'preenchido') {
    valor = '';
  } else if (ehBooleano) {
    valor = ` ${String(cond.valor) === 'true' ? 'Sim' : 'Não'}`;
  } else {
    valor = ` ${formatarValor(cond.valor) || '…'}`;
  }

  return `${labelGatilho(cond.chave, gatilhos)} ${labelOperador(cond.operador)}${valor}`;
}

/**
 * Texto legível de UMA regra (shape resolvido { efeito, conector, condicoes[] }).
 * Ex.: "fica obrigatório quando Tipo de Pessoa é igual a PJ".
 */
export function formatarRegra(regra, gatilhos = []) {
  const conds = regra.condicoes || [];
  const conector = String(regra.conector || 'E').toUpperCase() === 'OU' ? ' ou ' : ' e ';
  const cond = conds.map((c) => textoCondicao(c, gatilhos)).join(conector);
  const quando = cond ? `quando ${cond}` : '';

  if (regra.efeito === 'quantidade') {
    const max = regra.max == null || regra.max === '' ? '∞' : regra.max;
    return `exige no mín. ${regra.min || 0} e no máx. ${max} ${quando}`.trim();
  }
  if (regra.efeito === 'bloqueia_cadastro') {
    return `bloqueia o cadastro ${quando}`.trim();
  }
  if (regra.efeito === 'rotular') {
    return `renomeia para "${regra.rotulo || '…'}" ${quando}`.trim();
  }
  return `fica ${labelEfeito(regra.efeito)} ${quando}`.trim();
}

export function formatarRegras(regras = [], gatilhos = []) {
  return (regras || []).map((r) => formatarRegra(r, gatilhos));
}

/**
 * Texto SÓ das condições (sem o efeito/ação) — usado nos filtros do preview, onde
 * importa o CENÁRIO simulado, não a ação. Ex.: "Tipo de Pessoa (PF/PJ) é igual a PF".
 */
export function formatarCondicoes(condicoes = [], gatilhos = [], conector = 'E') {
  const sep = String(conector || 'E').toUpperCase() === 'OU' ? ' ou ' : ' e ';
  return (condicoes || []).map((c) => textoCondicao(c, gatilhos)).join(sep);
}

/**
 * Título humanizado de uma regra (1ª maiúscula + ponto) — usado como NOME do modelo
 * ao salvar. Ex.: "Fica oculto quando Tipo de Pessoa (PF/PJ) é igual a PF."
 */
export function tituloRegra(regra, gatilhos = []) {
  const t = formatarRegra(regra, gatilhos).trim();
  if (!t) return 'Regra';
  return `${t.charAt(0).toUpperCase()}${t.slice(1)}.`;
}

/**
 * Modelo é compatível com a entidade se TODA condicao.chave existir nos gatilhos dela.
 */
export function modeloCompativel(modelo, gatilhos = []) {
  const chaves = new Set((gatilhos || []).map((g) => g.campo));
  return (modelo.condicoes || []).every((c) => chaves.has(c.chave));
}

// Assinatura CANÔNICA (chaves em ordem fixa) — estável mesmo com o jsonb do backend
// reordenando as chaves das condições. Sem isto, "regra já é modelo" daria falso negativo.
const assinaturaRegra = (r) =>
  JSON.stringify({
    efeito: r.efeito,
    conector: String(r.conector || 'E').toUpperCase(),
    rotulo: r.rotulo ?? null,
    condicoes: (r.condicoes || []).map((c) => ({
      fonte: c.fonte || 'campo',
      chave: c.chave ?? null,
      operador: c.operador ?? null,
      valor: c.valor ?? null,
    })),
  });

/** A regra (resolvida) já corresponde a algum modelo? (esconde "salvar como modelo"). */
export function regraJaEhModelo(regra, modelos = []) {
  const alvo = assinaturaRegra(regra);
  return (modelos || []).some((m) => assinaturaRegra(m) === alvo);
}

/** Modelos compatíveis e AINDA NÃO presentes no campo (para o dropdown "Usar modelo"). */
export function modelosAplicaveis(modelos = [], gatilhos = [], regrasDoCampo = []) {
  const presentes = new Set((regrasDoCampo || []).map(assinaturaRegra));
  return (modelos || [])
    .filter((m) => modeloCompativel(m, gatilhos))
    .filter((m) => !presentes.has(assinaturaRegra(m)));
}

/** Converte um modelo numa regra aplicável a um campo (shape resolvido). */
export function modeloParaRegra(modelo) {
  return {
    efeito: modelo.efeito,
    conector: modelo.conector || 'E',
    rotulo: modelo.rotulo ?? null,
    condicoes: (modelo.condicoes || []).map((c) => ({ ...c })),
  };
}
